import { catchError, mergeMap, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, Store, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { ApiControlService } from '@medsurf/flat-services';
import * as FlatStates from '../../internal';
import { EntityBaseState } from '../base/entity.base.state';

export const MODALITY_ENTITY_TOKEN = new StateToken<ModalityEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.MODALITY_ENTITY);

export type ModalityEntityStateModel = FlatModels.EntityStatesModels.EntityStateModel<FlatModels.ModalityEntityModels.Modality>;

@State<ModalityEntityStateModel>({
  name: MODALITY_ENTITY_TOKEN,
  defaults: {
    entities: {},
  }
})
@Injectable()
export class ModalityEntityState extends EntityBaseState<FlatModels.ModalityEntityModels.Modality, ModalityEntityStateModel>{
  constructor(protected store: Store,
              protected apiControlService: ApiControlService) {
    super();
  }

  //<editor-fold desc="Selectors">

  @Selector([ModalityEntityState])
  static override typedEntities$(state: ModalityEntityStateModel): { [id: string]: FlatModels.ModalityEntityModels.Modality } {
    return EntityBaseState.typedEntities$(state);
  }
	
  @Selector([ModalityEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.ModalityEntityModels.Modality }): FlatModels.ModalityEntityModels.Modality[] {
    return EntityBaseState.entities$(typedEntities);
  }
	
  @Selector([ModalityEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.ModalityEntityModels.Modality }) {
    return EntityBaseState.entity$(typedEntities);
  }
	
  @Selector([ModalityEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.ModalityEntityModels.Modality }) {
    return EntityBaseState.entitiesByIds$(typedEntities);
  }
	
  @Selector([
    ModalityEntityState.typedEntities$,
    FlatStates.PageEntityState.currentSelectedSlidePage$
  ])
  static currentSelectedSlideModality$(typedEntities: { [id: string]: FlatModels.ModalityEntityModels.Modality },
                                       currentSelectedSlidePage: FlatModels.PageEntityModels.Slide | null) {
    if (currentSelectedSlidePage) {
      return typedEntities[currentSelectedSlidePage.modality as unknown as string];
    }
    return null;
  }
	
  @Selector([ModalityEntityState.entities$])
  static modalityArray$(entities: FlatModels.ModalityEntityModels.Modality[]): {name: string, value: string}[] {
    return entities.reduce((arr, modality) => {
      arr.push({
        value: modality.id,
        name: modality.name
      });
      return arr;
    }, [] as { value: string, name: string }[]);
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">
	
  @Action(FlatActions.ModalityEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<ModalityEntityStateModel>,
                            action: FlatActions.ModalityEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }
	
  @Action(FlatActions.ModalityEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<ModalityEntityStateModel>,
                              action: FlatActions.ModalityEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }
	
  @Action(FlatActions.ModalityEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<ModalityEntityStateModel>,
                               action: FlatActions.ModalityEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }
	
  @Action(FlatActions.ModalityEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<ModalityEntityStateModel>,
                                 action: FlatActions.ModalityEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">
	
  @Action(FlatActions.ModalityEntityActions.AddEntity)
  addEntity(_: StateContext<ModalityEntityStateModel>,
            {entity}: FlatActions.ModalityEntityActions.AddEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.ModalityEntityState.addEntity"
      );
    }

    // Add Modality
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.MODALITY_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }
	
  @Action(FlatActions.ModalityEntityActions.UpdateEntity)
  updateEntity(_: StateContext<ModalityEntityStateModel>,
               {entity}: FlatActions.ModalityEntityActions.UpdateEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.ModalityEntityState.updateEntity"
      );
    }

    // Update Modality
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.MODALITY_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }
	
  @Action(FlatActions.ModalityEntityActions.RemoveEntity)
  removeEntity(_: StateContext<ModalityEntityStateModel>,
               {entity}: FlatActions.ModalityEntityActions.RemoveEntity) {
    // Check Id
    if (!entity.id) {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.entity_has_no_id',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "LIBS.FlatStates.ModalityEntityState.removeEntity"
      );
    }

    // Remove Modality
    return this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.saving)).pipe(
      mergeMap(() =>
        this.apiControlService.sendMessage(
          new FlatActions.HistoryControlActions.SaveSnapshotsRequest([{
            stateToken: FlatModels.EntityControlModels.EntityQueryType.enum.MODALITY_ENTITY,
            modifierType: FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE,
            entity: entity,
            id: null,
            snapshotId: null
          }])
        )
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none))
      ),
      catchError((result) => {
        this.store.dispatch(new FlatActions.HistoryControlActions.SetBlockUiType(FlatModels.EntityControlModels.BlockUiType.enum.none));
        
        // Return error
        return throwError(() => {
          return result;
        });
      }),
    );
  }

  //</editor-fold>

  //</editor-fold>
}
